import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import InsuranceCard from '@components/Patient/InsuranceCard';
import { Switch } from '@components/primitives/Switch';
import { useUpdatePatientMutation } from '@features/patients/store/api';
import { useSubjectLanguageVariants } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import { JotBillIcon } from '@jotpsych/ui/icons/JotBillIcon';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Stack,
  type SxProps,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFeatureGate } from '@services/statsig';
import { useSnackbar } from 'notistack';

import SentryService from '../../../services/SentryService';
import type { InsuranceInfo } from '../../../types/types';
import {
  useCreatePatientCoverageMutation,
  useDeletePatientCoverageMutation,
  useUpdatePatientCoverageMutation,
} from '../store/api';
import {
  isSelfPayCoverage,
  mapInsuranceToCoverageRequest,
  useOrganizedPatientCoverages,
} from '../store/hooks';
import type { ClientErrorResponse, PatientCoverage } from '../types';

import { SelfPayCoverageCard, SelfPayCoverageToggle } from './SelfPayCoverageDesignation';

type CoverageSlot = 'primary_insurance' | 'secondary_insurance' | 'tertiary_insurance';

type CoverageAutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface PatientInsuranceAutosaveState {
  isDirty: boolean;
  lastSavedAt?: Date | null;
  status: CoverageAutosaveStatus;
}

interface PatientInsuranceEditorProps {
  errors?: Record<string, string>;
  highlightedFields?: string[];
  onAutosaveStateChange?: (state: PatientInsuranceAutosaveState) => void;
  onCoverageSave?: () => void;
  patientId: string;
  showInlineAutosaveStatus?: boolean;
  sx?: SxProps<Theme>;
}

const MISSING_FIELD_LABELS: Record<string, string> = {
  member_id: 'Member/Policy ID',
  payer_name: 'Payer Name',
  relationship_to_subscriber: 'Relationship',
};

function hasSelectedPayer(coverage?: InsuranceInfo | PatientCoverage | null): boolean {
  return Boolean(coverage?.payer_id || coverage?.payer_name);
}

export const PatientInsuranceEditor: React.FC<PatientInsuranceEditorProps> = ({
  errors = {},
  highlightedFields = [],
  onAutosaveStateChange,
  onCoverageSave,
  patientId,
  showInlineAutosaveStatus = false,
  sx,
}) => {
  const {
    billWithJotpsychResolved,
    billWithJotpsychSource,
    organized: organizedCoverages,
    refetch: refetchCoverages,
  } = useOrganizedPatientCoverages(patientId);

  const [createCoverage] = useCreatePatientCoverageMutation();
  const [updateCoverage] = useUpdatePatientCoverageMutation();
  const [deleteCoverage] = useDeletePatientCoverageMutation();
  const [updatePatient] = useUpdatePatientMutation();
  const { enqueueSnackbar } = useSnackbar();
  const subjectLanguageVariants = useSubjectLanguageVariants();
  const hasJotBillFeature = useFeatureGate('jotbill');

  const [showSecondaryInsurance, setShowSecondaryInsurance] = useState(false);
  const [showTertiaryInsurance, setShowTertiaryInsurance] = useState(false);
  const [hasPendingCoverageChanges, setHasPendingCoverageChanges] = useState(false);
  const [localCoverages, setLocalCoverages] = useState<{
    primary?: InsuranceInfo | PatientCoverage | null;
    secondary?: InsuranceInfo | PatientCoverage | null;
    tertiary?: InsuranceInfo | PatientCoverage | null;
  }>({});

  const pendingCoverageRef = useRef<{
    coverageOrder: number;
    insurance: InsuranceInfo | PatientCoverage | null;
  } | null>(null);

  useEffect(() => {
    setLocalCoverages({
      primary: organizedCoverages.primary,
      secondary: organizedCoverages.secondary,
      tertiary: organizedCoverages.tertiary,
    });
    setShowSecondaryInsurance(!!organizedCoverages.secondary);
    setShowTertiaryInsurance(!!organizedCoverages.tertiary);
    setHasPendingCoverageChanges(false);
  }, [organizedCoverages]);

  const performCoverageSave = useCallback(
    async (data: { coverageOrder: number; insurance: InsuranceInfo | PatientCoverage | null }) => {
      const { coverageOrder, insurance } = data;

      try {
        if (!insurance) {
          const existingCoverage =
            coverageOrder === 1
              ? organizedCoverages.primary
              : coverageOrder === 2
                ? organizedCoverages.secondary
                : organizedCoverages.tertiary;

          if (existingCoverage?.patient_insurance_coverage_id) {
            await deleteCoverage({
              patientId,
              coverageId: existingCoverage.patient_insurance_coverage_id,
            }).unwrap();
          }
        } else {
          const existingCoverage =
            coverageOrder === 1
              ? organizedCoverages.primary
              : coverageOrder === 2
                ? organizedCoverages.secondary
                : organizedCoverages.tertiary;

          const coverageData = mapInsuranceToCoverageRequest(coverageOrder, insurance);

          if (existingCoverage?.patient_insurance_coverage_id) {
            await updateCoverage({
              patientId,
              coverageId: existingCoverage.patient_insurance_coverage_id,
              updateCoverageRequest: coverageData,
            }).unwrap();
          } else {
            await createCoverage({
              patientId,
              createCoverageRequest: coverageData,
            }).unwrap();
          }
        }

        await refetchCoverages();
        setHasPendingCoverageChanges(false);
        onCoverageSave?.();
      } catch (error: unknown) {
        const errorData = (error as { data?: ClientErrorResponse })?.data;
        SentryService.logEvent('Error updating coverage', {
          level: 'error',
          tags: {
            patient_id: patientId,
            coverage_order: String(coverageOrder),
            error_code: errorData?.error_code || 'unknown',
          },
          extra: {
            error,
            error_message: errorData?.message,
          },
        });
        throw error;
      }
    },
    [
      createCoverage,
      deleteCoverage,
      onCoverageSave,
      organizedCoverages,
      patientId,
      refetchCoverages,
      updateCoverage,
    ],
  );

  const {
    triggerAutoSave: triggerCoverageAutoSave,
    saveImmediately: saveCoverageImmediately,
    status: coverageAutoSaveStatus,
    lastSavedAt: coverageAutoSaveLastSavedAt,
  } = useAutoSave<{ coverageOrder: number; insurance: InsuranceInfo | PatientCoverage | null }>({
    onSave: performCoverageSave,
    debounceMs: 2000,
    flushOnUnmount: true,
  });

  useEffect(() => {
    onAutosaveStateChange?.({
      isDirty: hasPendingCoverageChanges,
      lastSavedAt: coverageAutoSaveLastSavedAt,
      status: coverageAutoSaveStatus,
    });
  }, [
    coverageAutoSaveLastSavedAt,
    coverageAutoSaveStatus,
    hasPendingCoverageChanges,
    onAutosaveStateChange,
  ]);

  const handleInsuranceChange = useCallback(
    (insuranceType: CoverageSlot, insurance: InsuranceInfo | PatientCoverage | null) => {
      const coverageOrder =
        insuranceType === 'primary_insurance' ? 1 : insuranceType === 'secondary_insurance' ? 2 : 3;

      setLocalCoverages(prev => ({
        ...prev,
        [insuranceType === 'primary_insurance'
          ? 'primary'
          : insuranceType === 'secondary_insurance'
            ? 'secondary'
            : 'tertiary']: insurance,
      }));

      setHasPendingCoverageChanges(true);
      pendingCoverageRef.current = { coverageOrder, insurance };
      triggerCoverageAutoSave({ coverageOrder, insurance });
    },
    [triggerCoverageAutoSave],
  );

  const handleCoverageBlur = useCallback(() => {
    if (pendingCoverageRef.current && hasPendingCoverageChanges) {
      saveCoverageImmediately(pendingCoverageRef.current);
      pendingCoverageRef.current = null;
    }
  }, [hasPendingCoverageChanges, saveCoverageImmediately]);

  const handleSelfPaySaved = useCallback(() => {
    setHasPendingCoverageChanges(false);
    pendingCoverageRef.current = null;
    onCoverageSave?.();
  }, [onCoverageSave]);

  const handleBillWithJotpsychChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked ? 'enabled' : 'disabled';

      try {
        await updatePatient({
          patientId,
          updatePatientRequest: { bill_with_jotpsych: newValue },
        }).unwrap();
        await refetchCoverages();
      } catch (error) {
        enqueueSnackbar('Failed to update billing preference', { variant: 'error' });
        SentryService.captureException(error, {
          level: 'error',
          tags: {
            patient_id: patientId,
            field: 'bill_with_jotpsych',
          },
          extra: { error },
        });
      }
    },
    [enqueueSnackbar, patientId, refetchCoverages, updatePatient],
  );

  const primaryIsSelfPay = isSelfPayCoverage(localCoverages.primary);
  const showBillWithJotpsychBanner =
    hasJotBillFeature && !primaryIsSelfPay && hasSelectedPayer(localCoverages.primary);
  const billWithJotpsychTooltip =
    billWithJotpsychSource === 'org_payer_rule'
      ? 'Auto-enabled by payer rule'
      : billWithJotpsychSource === 'org_default'
        ? 'Set by organization default'
        : '';

  return (
    <Stack spacing={3} sx={sx}>
      {showInlineAutosaveStatus && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <AutosaveStatus
            status={coverageAutoSaveStatus}
            isDirty={hasPendingCoverageChanges}
            lastSavedAt={coverageAutoSaveLastSavedAt}
            verbose
          />
        </Box>
      )}

      <Box>
        <SelfPayCoverageToggle
          patientId={patientId}
          primaryCoverage={localCoverages.primary}
          refetchCoverages={refetchCoverages}
          onAfterSave={handleSelfPaySaved}
          onCoverageSave={onCoverageSave}
          containerSx={{ mb: 2 }}
        />

        {showBillWithJotpsychBanner && (
          <BillWithJotpsychBanner
            checked={billWithJotpsychResolved === true}
            sourceTooltip={billWithJotpsychTooltip}
            onChange={handleBillWithJotpsychChange}
          />
        )}
      </Box>

      {primaryIsSelfPay ? (
        <SelfPayCoverageCard subjectLabel={subjectLanguageVariants.singular} />
      ) : (
        <>
          <CoveragePendingWarning coverage={localCoverages.primary} />
          <InsuranceCard
            insurance={localCoverages.primary ?? null}
            onChange={insurance => handleInsuranceChange('primary_insurance', insurance)}
            onBlur={handleCoverageBlur}
            title="Primary Insurance"
            patientId={patientId}
            errors={errors}
            highlightedFields={highlightedFields}
          />
        </>
      )}

      {!primaryIsSelfPay && showSecondaryInsurance && (
        <Box>
          <CoveragePendingWarning coverage={localCoverages.secondary} />
          <InsuranceCard
            insurance={localCoverages.secondary ?? null}
            onChange={insurance => handleInsuranceChange('secondary_insurance', insurance)}
            onBlur={handleCoverageBlur}
            title="Secondary Insurance"
            patientId={patientId}
            showDelete
            onDelete={() => {
              setShowSecondaryInsurance(false);
              handleInsuranceChange('secondary_insurance', null);
            }}
            errors={errors}
            highlightedFields={highlightedFields}
          />
        </Box>
      )}

      {!primaryIsSelfPay && showTertiaryInsurance && (
        <Box>
          <CoveragePendingWarning coverage={localCoverages.tertiary} />
          <InsuranceCard
            insurance={localCoverages.tertiary ?? null}
            onChange={insurance => handleInsuranceChange('tertiary_insurance', insurance)}
            onBlur={handleCoverageBlur}
            title="Tertiary Insurance"
            patientId={patientId}
            showDelete
            onDelete={() => {
              setShowTertiaryInsurance(false);
              handleInsuranceChange('tertiary_insurance', null);
            }}
            errors={errors}
            highlightedFields={highlightedFields}
          />
        </Box>
      )}

      {!primaryIsSelfPay && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!showSecondaryInsurance && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowSecondaryInsurance(true)}
            >
              Add Secondary Insurance
            </Button>
          )}
          {showSecondaryInsurance && !showTertiaryInsurance && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowTertiaryInsurance(true)}
            >
              Add Tertiary Insurance
            </Button>
          )}
        </Box>
      )}
    </Stack>
  );
};

function BillWithJotpsychBanner({
  checked,
  sourceTooltip,
  onChange,
}: {
  checked: boolean;
  sourceTooltip: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '1px solid',
        borderColor: checked ? 'primary.light' : 'divider',
        borderRadius: 1.5,
        backgroundColor: checked ? 'action.selected' : 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
        <JotBillIcon
          aria-label="JotBill"
          fontSize="small"
          sx={{ mt: 0.25 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Bill with JotPsych
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign the note and let us handle the rest.
          </Typography>
          {sourceTooltip && (
            <Typography variant="caption" color="text.secondary">
              {sourceTooltip}
            </Typography>
          )}
        </Box>
      </Box>
      <Tooltip
        title={sourceTooltip}
        placement="left"
        arrow
        disableHoverListener={!sourceTooltip}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Switch
            checked={checked}
            onChange={onChange}
            slotProps={{ input: { 'aria-label': 'Bill with JotPsych' } }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
}

function CoveragePendingWarning({
  coverage,
}: {
  coverage?: InsuranceInfo | PatientCoverage | null;
}) {
  const missingFields =
    coverage &&
    'status' in coverage &&
    coverage.status === 'pending' &&
    'missing_fields' in coverage
      ? coverage.missing_fields
      : undefined;

  if (!missingFields?.length) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: 'warning.light',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <WarningAmberIcon color="warning" fontSize="small" />
      <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
        Coverage is pending. Missing:{' '}
        {missingFields.map(field => MISSING_FIELD_LABELS[field] || field).join(', ')}
      </Typography>
    </Box>
  );
}
